import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useTranslation from '@/hooks/useTranslation';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import Switch from '@/components/Inputs/Switch';

function FileAction({ icon, title, subtitle, onPress }) {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={title}
      accessibilityHint={subtitle}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={({ pressed }) => [styles.action, {
        backgroundColor: pressed ? colors.solid_color_tertiary
          : hovered ? colors.solid_color_secondary : 'transparent',
      }]}
    >
      <View style={[styles.icon, { backgroundColor: colors.solid_color_secondary }]}>
        <Icon name={icon} size={16} color={colors.text_color_primary} />
      </View>
      <View style={styles.copy}>
        <Text size="s" weight="medium" style={styles.title}>{title}</Text>
        <Text size="xs" color="secondary" style={styles.subtitle}>{subtitle}</Text>
      </View>
    </Pressable>
  );
}

export default function MacComposerToolsContent({
  hasUser, credits, onUpload, onLibrary, onBuyCredits, webSearchEnabled, onToggleWebSearch,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const divider = [styles.divider, { backgroundColor: colors.border_color_secondary }];

  return (
    <View style={styles.container}>
      {hasUser && (
        <>
          <FileAction
            icon="arrow-up-from-bracket"
            title={t('chatInput.files.uploadFile')}
            subtitle={t('chatInput.files.uploadSubtitle')}
            onPress={onUpload}
          />
          <FileAction
            icon="folder-open"
            title={t('chatInput.files.selectFromLibrary')}
            subtitle={t('chatInput.files.selectFromLibrarySubtitle')}
            onPress={onLibrary}
          />
          <View style={divider} />
        </>
      )}
      <View style={styles.searchRow}>
        <View style={styles.searchIcon}>
          <Icon name="earth-americas" size={16} color={colors.text_color_secondary} />
        </View>
        <Text size="s" style={[styles.title, styles.copy]}>{t('chatInput.tools.webSearch')}</Text>
        <Switch accessibilityLabel={t('chatInput.tools.webSearch')} value={webSearchEnabled} onValueChange={onToggleWebSearch} />
      </View>
      <View style={divider} />
      <View style={styles.creditsRow}>
        <Icon name="coins" size={13} color={colors.text_color_secondary} />
        <Text size="xs" color="secondary" style={[styles.systemText, styles.copy]}>
          {credits} {t('chatInput.credits.label')}
        </Text>
        <Pressable
          accessibilityRole="button"
          onPress={onBuyCredits}
          style={({ pressed }) => [styles.buyButton, {
            backgroundColor: pressed ? colors.solid_color_tertiary : colors.solid_color_secondary,
            borderColor: colors.border_color_secondary,
          }]}
        >
          <Text size="xs" weight="medium" style={styles.systemText}>{t('chatInput.credits.buyMore')}</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { paddingHorizontal: 6 },
  action: { flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 62, padding: 10, borderRadius: 8 },
  icon: { width: 32, height: 32, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  copy: { flex: 1, minWidth: 0 },
  systemText: { fontFamily: 'System' },
  title: { fontFamily: 'System', lineHeight: 18 },
  subtitle: { fontFamily: 'System', lineHeight: 16, marginTop: 2 },
  divider: { height: StyleSheet.hairlineWidth, marginHorizontal: 10, marginVertical: 6 },
  searchRow: { flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 40, paddingHorizontal: 10 },
  searchIcon: { width: 32, alignItems: 'center' },
  creditsRow: { flexDirection: 'row', alignItems: 'center', gap: 8, minHeight: 34, paddingHorizontal: 10 },
  buyButton: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6, borderWidth: StyleSheet.hairlineWidth },
});
